// src/store/customers/customersSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';
// The customer profile's Sales card (Task 13) moves a customer's pipeline
// stage with the SAME thunk instance the lead board uses, rather than a
// second thunk that hits the same endpoint - see the extraReducers block
// below. Two thunks calling move_stage would let the board's cached
// `state.leads.cards` silently disagree with `state.customers.currentCustomer`
// whenever a move happened from the profile instead of the board (fix round
// 1). leadsSlice only imports '@reduxjs/toolkit', apiService and
// '../errorMessage' - none of them import this file - so this is a
// one-directional dependency, not a cycle.
import { moveStage } from '../leads/leadsSlice';

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

// Arg is an id, or { id, silent: true } to refresh without raising isLoading
// (which would swap the whole profile for a skeleton and remount its cards).
export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (arg, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomer(typeof arg === 'object' ? arg.id : arg);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchCustomerHistory = createAsyncThunk(
  'customers/fetchCustomerHistory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomerHistory(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.createCustomer(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateCustomer(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteCustomer(id);
      return id;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

// Sales follow-up: activity log and to-dos
const thunk = (type, call) =>
  createAsyncThunk(type, async (arg, { rejectWithValue }) => {
    try {
      const response = await call(arg);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  });

export const fetchActivities = thunk('customers/fetchActivities',
  (id) => apiService.getCustomerActivities(id));
// POST also applies optional sales_status / lost_reason server-side.
export const createActivity = thunk('customers/createActivity',
  ({ id, data }) => apiService.createCustomerActivity(id, data));
export const fetchTasks = thunk('customers/fetchTasks',
  (params = {}) => apiService.getCustomerTasks(params));
export const createTask = thunk('customers/createTask',
  (data) => apiService.createCustomerTask(data));
export const updateTask = thunk('customers/updateTask',
  ({ id, data }) => apiService.updateCustomerTask(id, data));

// Initial state
const initialState = {
  customers: [],
  currentCustomer: null,
  history: [],
  activities: [],
  tasks: [],
  isLoading: false,
  isHistoryLoading: false,
  isSaving: false,
  // Separate from `isSaving`/`error` above (used by create/update/delete and
  // read by CustomerDetail's "customer not found" branch) so a failed
  // pipeline move on the profile can never be mistaken for a failed fetch.
  isPipelineActionSaving: false,
  pipelineActionError: null,
  error: null,
  successMessage: null
};

// Create slice
const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
      state.history = [];
    },
    clearPipelineActionError: (state) => {
      state.pipelineActionError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchCustomers
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // fetchCustomerById
      .addCase(fetchCustomerById.pending, (state, action) => {
        if (!action.meta.arg?.silent) state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCustomer = action.payload;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // fetchCustomerHistory
      .addCase(fetchCustomerHistory.pending, (state) => {
        state.isHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerHistory.fulfilled, (state, action) => {
        state.isHistoryLoading = false;
        state.history = action.payload;
      })
      .addCase(fetchCustomerHistory.rejected, (state, action) => {
        state.isHistoryLoading = false;
        state.error = action.payload;
      })

      // createCustomer
      .addCase(createCustomer.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state) => {
        state.isSaving = false;
        // No optimistic insert: state.customers holds a server-filtered result
        // set (search/distributor/lifecycle_stage/status filters, soft-deletes
        // excluded), so a newly created customer may not belong in the
        // currently active list. The list screen refetches after a
        // successful create instead.
        state.successMessage = 'Customer created successfully';
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // updateCustomer
      .addCase(updateCustomer.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.customers.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        state.currentCustomer = action.payload;
        state.successMessage = 'Customer updated successfully';
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // deleteCustomer
      .addCase(deleteCustomer.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.isSaving = false;
        state.customers = state.customers.filter((c) => c.id !== action.payload);
        if (state.currentCustomer && state.currentCustomer.id === action.payload) {
          state.currentCustomer = null;
        }
        state.successMessage = 'Customer deleted successfully';
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // moveStage - imported from leadsSlice (see the import comment above),
      // NOT redeclared here. This slice just also listens for the same
      // action type to drive its own isPipelineActionSaving/pipelineActionError
      // (kept separate from isSaving/error - see initialState comment) -
      // leadsSlice's own extraReducers (its `state.leads.cards` optimistic
      // update/rollback) run independently and are unaffected by this.
      .addCase(moveStage.pending, (state) => {
        state.isPipelineActionSaving = true;
        state.pipelineActionError = null;
      })
      .addCase(moveStage.fulfilled, (state) => {
        state.isPipelineActionSaving = false;
      })
      .addCase(moveStage.rejected, (state, action) => {
        state.isPipelineActionSaving = false;
        state.pipelineActionError = action.payload;
      })
      // activities and to-dos
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.activities = action.payload;
      })
      .addCase(createActivity.fulfilled, (state, action) => {
        state.isSaving = false;
        state.activities = [action.payload, ...state.activities];
        state.successMessage = 'Activity logged';
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.tasks = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isSaving = false;
        state.tasks = [action.payload, ...state.tasks];
        state.successMessage = 'To-do added';
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isSaving = false;
        state.tasks = state.tasks.map((t) => (t.id === action.payload.id ? action.payload : t));
      })
      .addMatcher(
        (action) => [createActivity, createTask, updateTask].some((t) => t.pending.match(action)),
        (state) => { state.isSaving = true; state.error = null; }
      )
      .addMatcher(
        (action) => [fetchActivities, fetchTasks, createActivity, createTask, updateTask]
          .some((t) => t.rejected.match(action)),
        (state, action) => { state.isSaving = false; state.error = action.payload; }
      );
  }
});

export const {
  clearError,
  clearSuccessMessage,
  clearCurrentCustomer,
  clearPipelineActionError,
} = customersSlice.actions;
export default customersSlice.reducer;
